import React, { useState, useEffect } from "react";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import { createColumnHelper } from "@tanstack/react-table";
import { Card, Box, TextField, InputAdornment } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

import { useTheme } from "@mui/material/styles";
import { ReactComponent as SearchIcon } from "assets/images/icons/icn_search.svg";

import TableField from "components/Table";

const ClientsDrawer = ({
  clientsData,
  setClientsData,
  formData,
  siteId,
  orgId,
  entId,
  hasManageOrg,
  getWirelessSSID,
}) => {
  const { dataMap, getData, decodeTokenData, sendRequest, sendBatch } =
    useDataSourceManager();
  const [clientsUuid, setClientsUuid] = useState(uuidv4());

  const theme = useTheme();

  const columnHelper = createColumnHelper();

  const columnsData = [
    columnHelper.accessor("clientName", {
      header: "Client",
    }),
    columnHelper.accessor("ipAddress", {
      header: "IP Address",
    }),
    columnHelper.accessor("clientId", {
      header: "MAC Address",
    }),
  ];

  const table = useTableInstance({
    tableName: "clients",
    data: clientsData,
    columns: columnsData,
    enableSorting: true,
    enablePagination: false,
    enableGlobalFilter: true,
    enableRowSelection: true,
  });

  const getClients = () => {
    const resourcePath = hasManageOrg
      ? "/ents/:entId/orgs/:orgId/sites/:siteId/settings/wirelessNetworks/:wirelessNetworkId/clients.read"
      : "/orgs/:orgId/sites/:siteId/settings/wirelessNetworks/:wirelessNetworkId/clients.read";

    const restfulParams = {
      entId,
      orgId,
      siteId,
      wirelessNetworkId: formData.id,
    };

    sendRequest(resourcePath, clientsUuid, restfulParams);
  };

  useEffect(() => {
    getClients();
  }, []);

  useEffect(() => {
    const response = getData(clientsUuid);

    if (response) {
      if (response.hasOwnProperty("code") && response.code !== 200) {
        // Error handling
      }else{
        const wirelessClients = response?.data ? response?.data.filter(client => client.ssid) : [];
        setClientsData(wirelessClients);
      }
    }
  }, [dataMap]);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 2,
          boxSizing: "border-box",
          gap: "20px",
        }}
      >
        <Box>
          <TextField
            fullWidth
            placeholder="Search"
            value={table.globalFilter ?? ""}
            onChange={(e) => table.setGlobalFilter(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{
              width: '700px',
              borderRadius: theme.shape.customBorderRadius[1],
              "& .MuiOutlinedInput-root": {
                height: 34,
                borderRadius: "inherit",
                fontSize: theme.typography.customFontSize[2],
                "& .MuiOutlinedInput-input": {
                  padding: "4px 8px",
                },
                "& fieldset": {
                  borderWidth: 1,
                  borderColor: theme.palette.primary.borderGreyPrimary,
                },
                "&:hover fieldset": {
                  borderWidth: 1,
                  borderColor: theme.palette.primary.mainHover,
                },
                "&.Mui-focused fieldset": {
                  borderWidth: 1,
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        </Box>
        <Card
          sx={{
            maxWidth: '700px',
            width: "100%",
            height: "100%",
            borderRadius: theme.shape.customBorderRadius[4],
            border: `1px solid  ${theme.palette.primary.greyThread}`,
          }}
        >
          <TableField {...table} />
        </Card>
      </Box>
    </>
  );
};
export default ClientsDrawer;
